<template>
    <div style="margin-top: 2px;">
        <template v-for="(item, i) in iconView" :key="item.key">
            <q-btn
                dense
                flat
                size="sm"
                color="grey-8"
                :icon="item.Icon"
                :disable="!item.isEnabled"
                v-show="item.isDisplayed"
                :class="{ 'hidden-element': !item.isVisible }"
                @click.stop="(e) => onIconClick(e, item, i)"
            >
                <q-tooltip anchor="bottom middle" self="top middle" transition-show="scale" transition-hide="scale">
                    <span style="font-size: larger;">
                        {{ item.Tooltip }}
                    </span>
                </q-tooltip>
                <q-menu
                    v-if="item.hasMenu"
                    v-model="item.menuOpen"
                    no-parent-event
                    anchor="bottom left"
                    self="top left"
                >
                    <q-list dense>
                        <q-item
                            v-for="(menu, mi) in item.MenuItems"
                            :key="mi"
                            clickable
                            v-ripple
                            :disable="!menu.isEnabled"
                            v-show="menu.isVisible"
                            :class="{ 'hidden-element': !menu.isVisible }"
                            @click="(e) => menuClick(e, menu)"
                        >
                            <q-item-section>{{ menu.title }}</q-item-section>
                        </q-item>
                    </q-list>
                </q-menu>
            </q-btn>
        </template>
    </div>
</template>

<script>
    import utils from '@/Shared/utils.jsx';
    import { QBtn, QTooltip, QMenu, QList, QItem, QItemSection } from 'quasar';

    export default {
        components: { QBtn, QTooltip, QMenu, QList, QItem, QItemSection },
        data: function () {
            return {
                iconView: [],
                // AG Grid refresh passes a new params object; Vue 3 props are read-only, so keep a mutable copy.
                cellParams: null,
            }
        },
        props: {
            params: {
                type: Object,
                default: null
            },
        },
        created() {
            this.cellParams = this.params;
            this.rebuildIconView();
        },
        methods: {
            // AG Grid Vue: returning true keeps this cell renderer instance alive across refreshes
            refresh(params) {
                this.cellParams = params;
                this.refreshIconFlags();
                return true;
            },
            rebuildIconView() {
                if (!this.cellParams || !this.cellParams.customdata || !this.cellParams.customdata.Icons) {
                    this.iconView = [];
                    return;
                }

                const priorOpen = {};
                for (const item of this.iconView) {
                    if (item.menuOpen)
                        priorOpen[item.key] = true;
                }

                this.iconView = this.cellParams.customdata.Icons.map((item, i) => {
                    const key = `${item.Icon || 'icon'}-${i}`;
                    const menuItems = item.MenuItems || [];
                    return {
                        key,
                        Icon: item.Icon,
                        Tooltip: item.Tooltip,
                        Actions: item.Actions,
                        hasMenu: menuItems.length > 0,
                        menuOpen: !!priorOpen[key],
                        isDisplayed: this.getIconDisplay(item),
                        isVisible: this.getIconVisible(item),
                        isEnabled: this.getIconEnabled(item),
                        _src: item,
                        MenuItems: menuItems.map(menu => ({
                            ...menu,
                            isVisible: this.getMenuVisible(menu),
                            isEnabled: this.getMenuEnabled(menu),
                            title: this.getMenuTitle(menu),
                            _src: menu,
                        })),
                    };
                });
            },
            // Mutate flags in place so QMenu is not torn down when PCAPExists (etc.) updates
            refreshIconFlags() {
                for (const item of this.iconView) {
                    const src = item._src;
                    if (!src)
                        continue;
                    item.isDisplayed = this.getIconDisplay(src);
                    item.isVisible = this.getIconVisible(src);
                    item.isEnabled = this.getIconEnabled(src);
                    for (const menu of item.MenuItems) {
                        const msrc = menu._src;
                        if (!msrc)
                            continue;
                        menu.isVisible = this.getMenuVisible(msrc);
                        menu.isEnabled = this.getMenuEnabled(msrc);
                        menu.title = this.getMenuTitle(msrc);
                    }
                }
            },
            getIconDisplay(item) {
                if (!item.displayExpn) {
                    return true;
                }
                return utils.evaluate(item.displayExpn, this.cellParams.customdata.context, false, false, false, this.cellParams.data);
            },
            getIconVisible(item) {
                if (!item.visibleExpn) {
                    return true;
                }
                return utils.evaluate(item.visibleExpn, this.cellParams.customdata.context, false, false, false, this.cellParams.data);
            },
            getIconEnabled(item) {
                if (!item.enabledExpn)
                    return true;
                return utils.evaluate(item.enabledExpn, this.cellParams.customdata.context, false, false, false, this.cellParams.data);
            },
            getMenuVisible(menu) {
                if (!menu.visibleExpn)
                    return true;
                return utils.evaluate(menu.visibleExpn, this.cellParams.customdata.context, false, false, false, this.cellParams.data);
            },
            getMenuEnabled(menu) {
                if (!menu.enabledExpn)
                    return true;
                return utils.evaluate(menu.enabledExpn, this.cellParams.customdata.context, false, false, false, this.cellParams.data);
            },
            getMenuTitle(menu) {
                return menu.titleExpn ? utils.evaluate(menu.titleExpn, this.cellParams.customdata.context, null, null, null, this.cellParams.data) : menu.Title;
            },
            async onIconClick(e, item, i) {
                if (item.hasMenu)
                    item.menuOpen = true;

                if (item.Actions) {
                    for (let action of item.Actions)
                        await utils.executeAction(action, { Data: this.cellParams.data }, this.cellParams.customdata.context, this.cellParams.customdata.controlscope, this.cellParams.customdata.scopeitems);
                }

                // Actions like doesFileExist → SetValue(PCAPExists) update enablement; refresh flags
                // without recreating the menu vnode tree, then keep the menu open.
                this.refreshIconFlags();
                if (item.hasMenu) {
                    await this.$nextTick();
                    item.menuOpen = true;
                }
            },
            async menuClick(e, menu) {
                if (!menu.Actions)
                    return;
                for (let action of menu.Actions)
                    await utils.executeAction(action, { Data: this.cellParams.data }, this.cellParams.customdata.context, this.cellParams.customdata.controlscope, this.cellParams.customdata.scopeitems);
            },
        }
    }
</script>

<style scoped>
    .hidden-element {
        visibility: hidden;
    }
</style>
